// File path: /modules/bridge/frontend/src/module.ts
// Bridge Module Entry Point for Shell App
// Special Education Compliance & Analytics

import React from 'react';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  FlagIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import type { ModuleEntryPoint, ModuleRouteConfig, ModuleNavigationItem } from '@core/types/module';
import {
  SPED_TEACHER_ROLES,
  SPED_DIRECTOR_ROLES,
  SCHOOL_ADMIN_ROLES,
  DISTRICT_ADMIN_ROLES,
  ALL_BRIDGE_ROLES,
} from './constants/roles';

// Lazy-loaded page components
const SpEdTeacherDashboard = React.lazy(() => import('./pages/spedteacher/SpEdTeacherDashboard'));
const SpEdDirectorDashboard = React.lazy(() => import('./pages/speddirector/SpEdDirectorDashboard'));
const PrincipalDashboard = React.lazy(() => import('./pages/principal/PrincipalDashboard'));

// Common pages
const StudentsPage = React.lazy(() => import('./pages/common/StudentsPage'));
const StudentProfilePage = React.lazy(() => import('./pages/common/StudentProfilePage'));
const DocumentsPage = React.lazy(() => import('./pages/common/DocumentsPage'));
const DocumentDetailPage = React.lazy(() => import('./pages/common/DocumentDetailPage'));
const GoalsPage = React.lazy(() => import('./pages/common/GoalsPage'));
const GoalDetailPage = React.lazy(() => import('./pages/common/GoalDetailPage'));
const ServicesPage = React.lazy(() => import('./pages/common/ServicesPage'));
const AccommodationsPage = React.lazy(() => import('./pages/common/AccommodationsPage'));
const ComplianceDashboardPage = React.lazy(() => import('./pages/common/ComplianceDashboardPage'));

/**
 * All routes for the Bridge module
 * Paths are relative to the module base path (/bridge)
 */
const routes: ModuleRouteConfig[] = [
  // Dashboard routes by role
  { path: 'spedteacher', element: SpEdTeacherDashboard, allowedRoles: SPED_TEACHER_ROLES },
  { path: 'schoolpsychologist', element: SpEdTeacherDashboard, allowedRoles: SPED_TEACHER_ROLES },
  { path: 'speddirector', element: SpEdDirectorDashboard, allowedRoles: SPED_DIRECTOR_ROLES },
  { path: 'schooladmin', element: PrincipalDashboard, allowedRoles: SCHOOL_ADMIN_ROLES },
  { path: 'principal', element: PrincipalDashboard, allowedRoles: SCHOOL_ADMIN_ROLES },
  { path: 'districtadmin', element: SpEdDirectorDashboard, allowedRoles: DISTRICT_ADMIN_ROLES },

  // Students
  { path: 'students', element: StudentsPage, allowedRoles: ALL_BRIDGE_ROLES, featureId: 'students' },
  { path: 'students/:studentId', element: StudentProfilePage, allowedRoles: ALL_BRIDGE_ROLES, featureId: 'students' },

  // Documents
  { path: 'documents', element: DocumentsPage, allowedRoles: SPED_TEACHER_ROLES, featureId: 'documents' },
  { path: 'documents/:documentId', element: DocumentDetailPage, allowedRoles: SPED_TEACHER_ROLES, featureId: 'documents' },

  // Goals
  { path: 'goals', element: GoalsPage, allowedRoles: SPED_TEACHER_ROLES, featureId: 'goals' },
  { path: 'goals/:goalId', element: GoalDetailPage, allowedRoles: SPED_TEACHER_ROLES, featureId: 'goals' },

  // Services & Accommodations
  { path: 'services', element: ServicesPage, allowedRoles: SPED_TEACHER_ROLES, featureId: 'services' },
  { path: 'accommodations', element: AccommodationsPage, allowedRoles: SPED_TEACHER_ROLES, featureId: 'services' },

  // Compliance
  { path: 'compliance', element: ComplianceDashboardPage, allowedRoles: SPED_DIRECTOR_ROLES, featureId: 'compliance' },
];

/**
 * Get navigation items based on user role
 */
function getNavigationItems(userRole: string): ModuleNavigationItem[] {
  const normalizedRole = userRole.toLowerCase();

  // Get dashboard path based on role
  const getDashboardPath = () => {
    switch (normalizedRole) {
      case 'spedteacher':
      case 'schoolpsychologist':
        return 'spedteacher';
      case 'speddirector':
        return 'speddirector';
      case 'schooladmin':
        return 'principal';
      case 'districtadmin':
      case 'programadmin':
      case 'stateadmin':
      case 'superadmin':
        return 'speddirector';
      default:
        return 'spedteacher';
    }
  };

  const baseItems: ModuleNavigationItem[] = [
    { name: 'Dashboard', href: getDashboardPath(), icon: HomeIcon, roles: ALL_BRIDGE_ROLES },
  ];

  switch (normalizedRole) {
    case 'spedteacher':
    case 'schoolpsychologist':
      return [
        ...baseItems,
        { name: 'My Students', href: 'students', icon: UsersIcon, roles: SPED_TEACHER_ROLES, featureId: 'students' },
        { name: 'IEP Documents', href: 'documents', icon: DocumentTextIcon, roles: SPED_TEACHER_ROLES, featureId: 'documents' },
        { name: 'Goals', href: 'goals', icon: FlagIcon, roles: SPED_TEACHER_ROLES, featureId: 'goals' },
        { name: 'Services', href: 'services', icon: AcademicCapIcon, roles: SPED_TEACHER_ROLES, featureId: 'services' },
        { name: 'Accommodations', href: 'accommodations', icon: ClipboardDocumentCheckIcon, roles: SPED_TEACHER_ROLES, featureId: 'services' },
      ];

    case 'speddirector':
      return [
        ...baseItems,
        { name: 'All Students', href: 'students', icon: UsersIcon, roles: SPED_DIRECTOR_ROLES, featureId: 'students' },
        { name: 'Documents', href: 'documents', icon: DocumentTextIcon, roles: SPED_DIRECTOR_ROLES, featureId: 'documents' },
        { name: 'Goals', href: 'goals', icon: FlagIcon, roles: SPED_DIRECTOR_ROLES, featureId: 'goals' },
        { name: 'Compliance', href: 'compliance', icon: ShieldCheckIcon, roles: SPED_DIRECTOR_ROLES, featureId: 'compliance' },
        { name: 'Services', href: 'services', icon: AcademicCapIcon, roles: SPED_DIRECTOR_ROLES, featureId: 'services' },
        { name: 'Analytics', href: 'speddirector', icon: ChartBarIcon, roles: SPED_DIRECTOR_ROLES },
      ];

    case 'schooladmin':
      return [
        ...baseItems,
        { name: 'Students', href: 'students', icon: UsersIcon, roles: SCHOOL_ADMIN_ROLES, featureId: 'students' },
        { name: 'Compliance', href: 'compliance', icon: ShieldCheckIcon, roles: SCHOOL_ADMIN_ROLES, featureId: 'compliance' },
        { name: 'Goals Overview', href: 'goals', icon: FlagIcon, roles: SCHOOL_ADMIN_ROLES, featureId: 'goals' },
      ];

    case 'districtadmin':
    case 'programadmin':
    case 'stateadmin':
    case 'superadmin':
      return [
        ...baseItems,
        { name: 'Students', href: 'students', icon: UsersIcon, roles: DISTRICT_ADMIN_ROLES, featureId: 'students' },
        { name: 'Documents', href: 'documents', icon: DocumentTextIcon, roles: DISTRICT_ADMIN_ROLES, featureId: 'documents' },
        { name: 'Compliance', href: 'compliance', icon: ShieldCheckIcon, roles: DISTRICT_ADMIN_ROLES, featureId: 'compliance' },
        { name: 'Services', href: 'services', icon: AcademicCapIcon, roles: DISTRICT_ADMIN_ROLES, featureId: 'services' },
        { name: 'Analytics', href: 'speddirector', icon: ChartBarIcon, roles: DISTRICT_ADMIN_ROLES },
      ];

    default:
      return baseItems;
  }
}

/**
 * Get the default route for a user based on their role
 */
function getDefaultRoute(userRole: string): string {
  const normalizedRole = userRole.toLowerCase();
  switch (normalizedRole) {
    case 'spedteacher':
    case 'schoolpsychologist':
      return 'spedteacher';
    case 'speddirector':
      return 'speddirector';
    case 'schooladmin':
      return 'principal';
    case 'districtadmin':
    case 'programadmin':
    case 'stateadmin':
    case 'superadmin':
      return 'speddirector';
    default:
      return 'spedteacher';
  }
}

/**
 * Bridge module entry point
 */
const BridgeModule: ModuleEntryPoint = {
  moduleId: 'bridge',
  routes,
  getNavigationItems,
  getDefaultRoute,
};

export default BridgeModule;
export { getDefaultRoute, getNavigationItems, routes };
